/**
 * Device-only Onthouden drafts (spec §8.2: local draft, labelled).
 *
 * Drafts live in localStorage under 'rust-remember-draft', keyed per open note
 * (server id, or 'new' for an unsaved note). A draft survives a reload while
 * the cloud save is unconfirmed; it is NEVER presented as cloud-saved — the UI
 * labels it 'Alleen op dit apparaat' until repositories.notes confirms.
 */
import type { SyncSaveState } from '@/types'

export const REMEMBER_DRAFT_STORAGE_KEY = 'rust-remember-draft'
/** Draft key for a note that has no server id yet. */
export const NEW_NOTE_DRAFT_KEY = 'new'

export type NoteDraft = {
  /** Server id when editing an existing note; null for a new note. */
  noteId: string | null
  title: string
  content: string
  category: string
  pinned: boolean
  urgent: boolean
  state: SyncSaveState
  updatedAt: string
}

export type NoteDraftMap = Record<string, NoteDraft>

export function draftKeyFor(noteId: string | null): string {
  return noteId ?? NEW_NOTE_DRAFT_KEY
}

function storageAvailable(): boolean {
  return typeof localStorage !== 'undefined'
}

const VALID_STATES: readonly SyncSaveState[] = [
  'local_saved',
  'sync_queued',
  'syncing',
  'cloud_saved',
  'sync_failed',
]

function isValidDraft(value: unknown): value is NoteDraft {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  return (
    (typeof candidate.noteId === 'string' || candidate.noteId === null) &&
    typeof candidate.title === 'string' &&
    typeof candidate.content === 'string' &&
    typeof candidate.category === 'string' &&
    typeof candidate.pinned === 'boolean' &&
    typeof candidate.urgent === 'boolean' &&
    typeof candidate.state === 'string' &&
    VALID_STATES.includes(candidate.state as SyncSaveState) &&
    typeof candidate.updatedAt === 'string'
  )
}

export function loadNoteDrafts(): NoteDraftMap {
  if (!storageAvailable()) return {}
  try {
    const raw = localStorage.getItem(REMEMBER_DRAFT_STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return {}
    const drafts: NoteDraftMap = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (isValidDraft(value)) drafts[key] = value
    }
    return drafts
  } catch {
    // Corrupt or unreadable storage: start clean rather than crashing the page.
    return {}
  }
}

export function writeNoteDrafts(drafts: NoteDraftMap): void {
  if (!storageAvailable()) return
  try {
    localStorage.setItem(REMEMBER_DRAFT_STORAGE_KEY, JSON.stringify(drafts))
  } catch {
    // Storage full or blocked: the in-memory draft still protects this session.
  }
}

export function saveNoteDraft(key: string, draft: NoteDraft): NoteDraftMap {
  const drafts = loadNoteDrafts()
  drafts[key] = draft
  writeNoteDrafts(drafts)
  return drafts
}

export function removeNoteDraft(key: string): NoteDraftMap {
  const drafts = loadNoteDrafts()
  delete drafts[key]
  writeNoteDrafts(drafts)
  return drafts
}
